import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import {
  X,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe,
  Loader2,
  Phone,
  Video,
} from 'lucide-react';
import { useBookingSlots, useBookMeeting, useRescheduleMeeting } from '../../../services/calendlyBooking';
import { useConfirmBooking } from '../../../services/onboardingBoard';
import { CT, ymd, todayYmd } from '../../../lib/dateRange';
import { ensureAuthorized, getAccessToken, openAuthorizePopup } from '../../../lib/ringcentral/auth';

/**
 * The slot picker (in-app meetings since 2026-10-25, Calendly out). Open
 * times come from our own data (the host's hours, lunch, days off and
 * meetings already booked) plus the host's Outlook calendar when Microsoft
 * 365 is connected (2026-10-28: "Outlook included" in the header; a note
 * when it couldn't be read). Phone by default (we call them at the time);
 * picking Video books a RingCentral Video meeting under the staff member's
 * RingCentral sign-in, with the join link in the veteran's email, texts and
 * dashboard. `reschedule` ({ bookingId, kind, timezone }) moves an existing
 * meeting, starting on its timezone.
 *
 * Mounts:
 *   'board'      LeadSlideOver: org calendar; the booking stays with the
 *                Sales Rep (Meeting set, Signing Up).
 *   'callcenter' Call Command Center — org calendar; NO confirm offer
 *                (confirming mid-call yanks the lead off the board while the
 *                specialist is still on the phone — 2026-08-15 rule).
 *
 * Booking is TWO-TAP: tapping a time arms one emphasized "Book {time}"
 * button; tapping anywhere else disarms. No accidental bookings.
 *
 */

// The display-timezone choices. Defaults to CT (the org
// clock) because nothing stored reliably knows the veteran's zone — the
// specialist flips this on the call ("what time zone are you in?") and the
// choice ALSO rides into the booking as the veteran's timezone, so their
// confirmation + reminders render in the veteran's own clock.
const ZONES = [
  { tz: 'America/New_York', label: 'Eastern Time - US & Canada' },
  { tz: 'America/Chicago', label: 'Central Time - US & Canada' },
  { tz: 'America/Denver', label: 'Mountain Time - US & Canada' },
  { tz: 'America/Phoenix', label: 'Arizona' },
  { tz: 'America/Los_Angeles', label: 'Pacific Time - US & Canada' },
  { tz: 'America/Anchorage', label: 'Alaska' },
  { tz: 'Pacific/Honolulu', label: 'Hawaii' },
];

// Why a time is taken, on its greyed tile.
const TAKEN_WHY = { meeting: 'Booked', outlook: 'Outlook', lunch: 'Lunch' };

// Fixed skeleton so the card never reflows while a week loads: a full grid
// of stubs (fills the whole slot area) with a shimmer sweep so it clearly
// reads as "loading", not "empty".
const SlotSkeleton = () => (
  <div className="grid grid-cols-3 gap-2">
    {Array.from({ length: 15 }).map((_, i) => (
      <div
        key={i}
        className="h-9 animate-shimmer rounded-lg bg-gradient-to-r from-slate-100 via-slate-200/80 to-slate-100 bg-[length:200%_100%] dark:from-white/5 dark:via-white/15 dark:to-white/5"
      />
    ))}
  </div>
);

const SlotPickerOverlay = ({
  mount = 'board',
  leadId,
  leadName,
  onClose,
  onBooked,
  onConfirmed,
  // Optional booking window { fromMs, toMs, label } (Nic 2026-09-24: a
  // veteran who wants to review books within the next 2 days (tomorrow or the day after, 2026-10-27)). Only slots inside
  // it show until the rep asks for all times.
  within = null,
  // Moving a meeting: { bookingId, kind } of the one being moved.
  reschedule = null,
}) => {
  // 'onboarding' books a signed client (leadId = their onboardingId).
  const apiMount = mount === 'onboarding' ? 'onboarding' : 'board';

  // null = the week starting today; paging moves in whole weeks.
  const [weekStart, setWeekStart] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [armed, setArmed] = useState(null); // startTime of the armed slot
  const [taken, setTaken] = useState(false); // "just taken" amber notice
  const [booked, setBooked] = useState(null); // success payload
  const [bookError, setBookError] = useState(null); // {msg, code}
  const [confirmError, setConfirmError] = useState(null);
  // Phone by default; Video books a RingCentral Video meeting.
  const [video, setVideo] = useState(reschedule?.kind === 'video');
  const [rcNeeded, setRcNeeded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const windowed = !!within && !showAll;

  // Display timezone: CT until the specialist flips it (see ZONES above). A
  // reschedule starts on the meeting's own zone (QA 2026-10-26), and keeps
  // it unless the specialist picks another: with no zone passed in, the
  // booking sends none and the server keeps the old one.
  const meetingTz =
    reschedule?.timezone && moment.tz.zone(reschedule.timezone) ? reschedule.timezone : null;
  // Opens on the viewer's own clock when it is one of the zones offered
  // (Nic 2026-10-28: the calendar reads in the browser's clock, so the
  // picker should too), else Central. The rep still flips to the
  // veteran's zone on the call, and that choice rides into the booking.
  const viewerTz = ZONES.some((z) => z.tz === moment.tz.guess()) ? moment.tz.guess() : CT;
  const [displayTz, setDisplayTzState] = useState(meetingTz || viewerTz);
  const [tzPicked, setTzPicked] = useState(false);
  const setDisplayTz = (tz) => {
    setTzPicked(true);
    setDisplayTzState(tz);
  };
  const [tzOpen, setTzOpen] = useState(false);

  const slotsQuery = useBookingSlots({
    mount: apiMount,
    leadId,
    start: weekStart,
  });
  const data = slotsQuery.data;
  // Rebucket the fetched week into the DISPLAY zone's calendar days. Slot
  // times are absolute instants, so switching zones is pure re-labeling —
  // no refetch. The server's window is CT-bounded, so a late-night CT slot
  // belongs to the NEXT day in an eastern zone: the strip renders whatever
  // days the conversion yields (7, sometimes 8).
  // Taken times ride along (Nic 2026-10-28): the rest of the host's working
  // day, greyed with why, re-bucketed by the same instants.
  const days = useMemo(() => {
    const fetched = data?.days || [];
    if (!fetched.length) return [];
    const byDay = new Map();
    const inWindow = (slot) => {
      if (!windowed) return true;
      const at = new Date(slot.startTime).getTime();
      return at >= within.fromMs && at <= within.toMs;
    };
    const bucket = (list, field) => {
      for (const slot of list.filter(inWindow)) {
        const key = moment.tz(slot.startTime, displayTz).format('YYYY-MM-DD');
        if (!byDay.has(key)) byDay.set(key, { slots: [], taken: [] });
        byDay.get(key)[field].push(slot);
      }
    };
    bucket(fetched.flatMap((d) => d.slots), 'slots');
    bucket(fetched.flatMap((d) => d.taken || []), 'taken');
    const windowStart = moment.tz(fetched[0].date, 'YYYY-MM-DD', CT);
    const windowEnd = windowStart.clone().add(fetched.length, 'days');
    const cursor = windowStart.clone().tz(displayTz).startOf('day');
    const lastDay = windowEnd
      .clone()
      .subtract(1, 'millisecond')
      .tz(displayTz)
      .startOf('day');
    const out = [];
    while (cursor.valueOf() <= lastDay.valueOf()) {
      const date = cursor.format('YYYY-MM-DD');
      const got = byDay.get(date) || { slots: [], taken: [] };
      out.push({ date, slots: got.slots, taken: got.taken });
      cursor.add(1, 'day');
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, displayTz, windowed, within?.fromMs, within?.toMs]);

  // Auto-select the first day with slots whenever a new week lands.
  useEffect(() => {
    if (!days.length) return;
    setSelectedDay((current) => {
      if (current && days.some((d) => d.date === current && d.slots.length)) {
        return current;
      }
      const first = days.find((d) => d.slots.length);
      return first ? first.date : days[0].date;
    });
  }, [days]);

  const book = useBookMeeting();
  const move = useRescheduleMeeting();
  const confirm = useConfirmBooking();
  const booking = reschedule ? move : book;
  const busy = booking.isLoading || confirm.isLoading;

  // Escape closes — suppressed while a booking is in flight.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const bookSlot = async (startTime) => {
    setTaken(false);
    setBookError(null);
    setRcNeeded(false);
    let rcAccessToken = null;
    if (video) {
      // The video meeting is created under the staff member's RingCentral
      // account, so they must be signed in (the phone already needs it).
      const ok = await ensureAuthorized();
      if (!ok) {
        setArmed(null);
        setRcNeeded(true);
        return;
      }
      rcAccessToken = getAccessToken();
    }
    booking.mutate(
      {
        mount: apiMount,
        leadId,
        ...(reschedule ? { bookingId: reschedule.bookingId } : {}),
        startTime,
        timezone: reschedule && !meetingTz && !tzPicked ? null : displayTz,
        video,
        rcAccessToken,
      },
      {
        onSuccess: (res) => {
          setBooked(res);
          onBooked?.(res);
        },
        onError: (err) => {
          const body = err?.response?.data || {};
          setArmed(null);
          if (body.code === 'slot_taken') {
            setTaken(true);
            slotsQuery.refetch();
            return;
          }
          if (body.code === 'rc_login') {
            setRcNeeded(true);
            return;
          }
          setBookError({
            msg: body.msg || "The booking didn't go through. Try again.",
            code: body.code || null,
          });
        },
      },
    );
  };

  const confirmBooking = () => {
    setConfirmError(null);
    confirm.mutate(
      {
        cadenceRunId: booked.cadenceRunId,
        calendlyBookingId: booked.booking.calendlyBookingId,
      },
      {
        onSuccess: () => {
          onConfirmed?.();
          onClose?.();
        },
        // The booking itself is fine either way — a failed confirm (the run
        // raced to another outcome, say) must say so, not silently reset.
        onError: (err) => {
          setConfirmError(
            err?.response?.data?.msg ||
              "Couldn't confirm from here. Handle it from the tile.",
          );
        },
      },
    );
  };

  const weekAnchorYmd = weekStart || todayYmd();
  const canPageBack = weekAnchorYmd > todayYmd();
  // Forward horizon: 8 weeks out is far enough for a follow-up or a first
  // call. The chevron stops there.
  const maxAheadYmd = ymd(moment.tz(CT).add(8 * 7, 'days'));
  const canPageForward = weekAnchorYmd < maxAheadYmd;
  const pageWeek = (dir) => {
    const next = ymd(
      moment
        .tz(weekAnchorYmd, 'YYYY-MM-DD', CT)
        .add(dir * 7, 'days'),
    );
    if (dir > 0 && next > maxAheadYmd) return;
    setArmed(null);
    setTaken(false);
    setWeekStart(next <= todayYmd() ? null : next);
  };

  const queryError = slotsQuery.error?.response?.data || null;
  const queryStatus = slotsQuery.error?.response?.status || null;

  const selected = days.find((d) => d.date === selectedDay) || null;
  const dayLabel = (date) => {
    const m = moment.tz(date, 'YYYY-MM-DD', displayTz);
    return {
      dow: m.format('ddd'),
      dom: m.format('D'),
    };
  };
  const timeLabel = (iso) => moment.tz(iso, displayTz).format('h:mm A');
  const dateLabel = (iso) => moment.tz(iso, displayTz).format('ddd, MMM D');
  const tzAbbr = moment.tz(displayTz).zoneAbbr();
  // The abbreviation AT the slot's instant, not now's: a November booking
  // made in late October must read EST, not EDT.
  const tzAbbrAt = (iso) => moment.tz(iso, displayTz).zoneAbbr();

  // ---- Success view --------------------------------------------------------
  const successView = booked ? (
    <div className="flex flex-col items-center px-2 py-6 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15">
        <Check size={26} className="text-emerald-600 dark:text-emerald-400" />
      </div>
      <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
        {reschedule ? 'Moved' : 'Booked'} - {dateLabel(booked.booking.startTime)} at{' '}
        {timeLabel(booked.booking.startTime)} {tzAbbrAt(booked.booking.startTime)}
        {booked.booking.hostName ? ` with ${booked.booking.hostName}` : ''}
      </div>
      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {booked.booking.kind === 'video'
          ? 'A video meeting. They get the join link by email and text.'
          : 'A phone call: you call them at that time. They get the details by email and text.'}
        {mount === 'onboarding'
          ? ' It shows on your Meetings calendar and their dashboard.'
          : ' It shows on their card in Signing Up.'}
      </div>
      {mount === 'board' && booked.confirmable ? (
        <div className="mt-4 flex flex-col items-center gap-2">
          <button
            type="button"
            disabled={confirm.isLoading}
            onClick={confirmBooking}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-60"
          >
            {confirm.isLoading ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Check size={13} />
            )}
            Confirm booking
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] font-medium text-slate-400 transition-colors hover:text-slate-500 dark:hover:text-slate-300"
          >
            Not now
          </button>
          {confirmError ? (
            <div className="rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
              {confirmError}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700 dark:bg-white/10 dark:hover:bg-white/15"
          >
            Done
          </button>
        </>
      )}
    </div>
  ) : null;

  // Plain 409s (nobody owns the lead) and other load failures.
  const loadErrorView =
    !booked && slotsQuery.isError ? (
      <div className="flex flex-col items-center px-4 py-8 text-center">
        <div className="mb-3 text-sm font-medium text-slate-600 dark:text-slate-300">
          {queryError?.msg || "Couldn't load open times."}
        </div>
        {queryStatus === 429 || !queryStatus || queryStatus >= 500 ? (
          <button
            type="button"
            onClick={() => slotsQuery.refetch()}
            className="rounded-lg bg-slate-800 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700 dark:bg-white/10 dark:hover:bg-white/15"
          >
            Try again
          </button>
        ) : null}
      </div>
    ) : null;

  // ---- The picker ----------------------------------------------------------
  const weekEmpty = days.length > 0 && days.every((d) => !d.slots.length);
  // Week paging in flight: the strip flips to the TARGET week's dates
  // immediately and the grid shimmers — a click must never look inert.
  const paging = slotsQuery.isFetching && slotsQuery.isPreviousData;
  const stripDays =
    !paging && days.length
      ? days
      : Array.from({ length: 7 }).map((_, i) => ({
          date: moment
            .tz(weekAnchorYmd, 'YYYY-MM-DD', CT)
            .tz(displayTz)
            .startOf('day')
            .add(i, 'days')
            .format('YYYY-MM-DD'),
          slots: [],
          placeholder: true,
        }));
  const pickerView =
    !booked && !loadErrorView ? (
      <>
        {/* Phone (we call them) or Video (a RingCentral join link). */}
        <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
          {[
            { on: false, icon: Phone, label: 'Phone call', sub: 'You call them' },
            { on: true, icon: Video, label: 'Video meeting', sub: 'RingCentral link' },
          ].map((o) => {
            const Icon = o.icon;
            const sel = video === o.on;
            return (
              <button
                key={o.label}
                type="button"
                aria-pressed={sel}
                onClick={(e) => {
                  e.stopPropagation();
                  setVideo(o.on);
                  setRcNeeded(false);
                }}
                className={`flex items-center justify-center gap-2 rounded-lg px-2 py-1.5 text-left ${
                  sel
                    ? 'bg-white text-slate-900 shadow-sm dark:!bg-white/15 dark:!text-white'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Icon size={14} className="shrink-0" />
                <span className="leading-tight">
                  <span className="block text-xs font-semibold">{o.label}</span>
                  <span className="block text-[10px] opacity-70">{o.sub}</span>
                </span>
              </button>
            );
          })}
        </div>
        {rcNeeded ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
            <span>Sign in to RingCentral to book a video meeting.</span>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                const ok = await openAuthorizePopup().catch(() => false);
                if (ok) setRcNeeded(false);
              }}
              className="shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-500"
            >
              Sign in
            </button>
          </div>
        ) : null}
        {within ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-lg bg-sky-50 px-3 py-2 text-[11px] font-medium text-sky-800 dark:bg-sky-500/10 dark:text-sky-300">
            <span>{showAll ? 'Showing all open times.' : within.label}</span>
            <button
              type="button"
              onClick={() => {
                setArmed(null);
                setShowAll((v) => !v);
              }}
              className="shrink-0 whitespace-nowrap font-semibold underline-offset-2 hover:underline"
            >
              {showAll ? 'Only the next 2 days' : 'Show all times'}
            </button>
          </div>
        ) : null}
        {/* Day strip: 7 CT days + week chevrons. */}
        <div className="mb-3 flex items-center gap-1">
          <button
            type="button"
            disabled={!canPageBack}
            onClick={() => pageWeek(-1)}
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-white/10"
            title="Previous week"
          >
            <ChevronLeft size={16} />
          </button>
          <div
            className="grid flex-1 gap-1"
            // Zone conversion can yield 8 calendar days for one CT week.
            style={{
              gridTemplateColumns: `repeat(${stripDays.length}, minmax(0, 1fr))`,
            }}
          >
            {stripDays.map((d) => {
              const { dow, dom } = dayLabel(d.date);
              const isSel = d.date === selectedDay;
              return (
                <button
                  key={d.date}
                  type="button"
                  disabled={d.placeholder}
                  onClick={() => {
                    setSelectedDay(d.date);
                    setArmed(null);
                  }}
                  // No color fade and no hover tint on the selected day (Nic
                  // 2026-09-30): the old day fading out while the hover grey
                  // hid the new one read as the previous pick flashing back.
                  className={`flex flex-col items-center rounded-lg py-1.5 ${
                    isSel
                      ? 'bg-slate-800 text-white hover:bg-slate-800 dark:bg-white/15 dark:hover:bg-white/15'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10'
                  }`}
                >
                  <span className="text-[10px] font-medium uppercase opacity-70">
                    {dow}
                  </span>
                  <span className="text-sm font-bold leading-tight">{dom}</span>
                  <span
                    className={`mt-0.5 h-1 w-1 rounded-full ${
                      d.slots.length
                        ? isSel
                          ? 'bg-emerald-400'
                          : 'bg-emerald-500'
                        : 'bg-transparent'
                    }`}
                  />
                </button>
              );
            })}
          </div>
          <button
            type="button"
            disabled={!canPageForward}
            onClick={() => pageWeek(1)}
            className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-white/10"
            title="Next week"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {taken ? (
          <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
            That time was just taken. Here's what's still open.
          </div>
        ) : null}
        {/* Outlook is connected but didn't answer (2026-10-28): these times
            come from our bookings alone, so say so rather than book blind. */}
        {data?.outlook?.configured && !data.outlook.checked ? (
          <div className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
            Outlook couldn't be checked. These times are from our bookings only.
          </div>
        ) : null}
        {bookError ? (
          <div className="mb-2 rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 dark:bg-rose-500/10 dark:text-rose-400">
            {bookError.msg}
          </div>
        ) : null}

        {/* Slot grid: fixed-height scroll region, three columns, two-tap. */}
        <div className="h-56 overflow-y-auto pr-0.5">
          {slotsQuery.isLoading || paging ? (
            <SlotSkeleton />
          ) : weekEmpty ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Calendar size={20} className="mb-2 text-slate-300 dark:text-slate-600" />
              {windowed ? (
                // The window can be fully booked (or fall on a weekend):
                // say so and offer the way out, never a dead end.
                <>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    Nothing open in that window.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setArmed(null);
                      setShowAll(true);
                    }}
                    className="mt-2 inline-flex h-8 items-center whitespace-nowrap rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-700 dark:!bg-slate-100 dark:!text-slate-900"
                  >
                    Show all times
                  </button>
                </>
              ) : (
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Nothing open this week. Try the next one.
                </div>
              )}
            </div>
          ) : selected && !selected.slots.length && !(selected.taken || []).length ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Nothing open this day.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {selected && !selected.slots.length ? (
                <div className="col-span-3 py-1 text-center text-[11px] font-medium text-slate-400">
                  Nothing open this day.
                </div>
              ) : null}
              {[
                ...(selected?.slots || []).map((s) => ({ ...s, open: true })),
                ...(selected?.taken || []).map((s) => ({ ...s, open: false })),
              ]
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                .map((slot) => {
                if (!slot.open) {
                  // Taken: greyed, with why, never bookable.
                  return (
                    <div
                      key={slot.startTime}
                      aria-disabled="true"
                      title={`${timeLabel(slot.startTime)}: ${TAKEN_WHY[slot.reason] || 'Taken'}`}
                      className="flex h-9 flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 leading-none text-slate-400 dark:border-white/10 dark:!bg-white/[0.03] dark:text-slate-500"
                    >
                      <span className="text-xs font-semibold line-through">{timeLabel(slot.startTime)}</span>
                      <span className="mt-[3px] text-[9px] font-bold uppercase tracking-wide">{TAKEN_WHY[slot.reason] || 'Taken'}</span>
                    </div>
                  );
                }
                const isArmed = armed === slot.startTime;
                return isArmed ? (
                  <button
                    key={slot.startTime}
                    type="button"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      bookSlot(slot.startTime);
                    }}
                    className="col-span-1 inline-flex h-9 items-center justify-center gap-1 rounded-lg bg-emerald-600 text-xs font-bold text-white transition-colors hover:bg-emerald-500 disabled:opacity-60"
                  >
                    {booking.isLoading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : null}
                    {reschedule ? 'Move to' : 'Book'} {timeLabel(slot.startTime)}
                  </button>
                ) : (
                  <button
                    key={slot.startTime}
                    type="button"
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      setArmed(slot.startTime);
                    }}
                    className="h-9 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
                  >
                    {timeLabel(slot.startTime)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Time zone: pick the VETERAN's zone on the call. Every time above
            re-labels instantly, and the choice rides into the booking so
            their email, texts and dashboard read in their own clock. Custom
            dropdown (opens UPWARD from the card's footer): the native
            select's OS-rendered popup can't match the card. */}
        <div className="relative mt-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setTzOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10"
          >
            <Globe size={12} className="shrink-0 text-slate-400" />
            {ZONES.find((z) => z.tz === displayTz)?.label || displayTz} (
            {moment.tz(displayTz).format('h:mma')})
            <ChevronDown
              size={12}
              className={`transition-transform ${tzOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {tzOpen ? (
            <div
              className="absolute bottom-full left-0 z-10 mb-1.5 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:!border-white/10 dark:!bg-surface-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              {ZONES.map((z) => {
                const isSel = z.tz === displayTz;
                return (
                  <button
                    key={z.tz}
                    type="button"
                    onClick={() => {
                      setDisplayTz(z.tz);
                      setArmed(null);
                      setTzOpen(false);
                    }}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[11px] font-medium transition-colors ${
                      isSel
                        ? 'bg-sky-600 text-white'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                  >
                    <span className="truncate">{z.label}</span>
                    <span
                      className={
                        isSel ? 'text-sky-100' : 'text-slate-400'
                      }
                    >
                      {moment.tz(z.tz).format('h:mma')}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </>
    ) : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1080] flex items-end justify-center sm:items-center"
      // Tap-away inside the card disarms; the backdrop below closes.
      onClick={() => setArmed(null)}
    >
      <div
        className="absolute inset-0 bg-slate-900/30 dark:bg-black/60"
        aria-hidden
        onClick={() => {
          if (!busy) onClose?.();
        }}
      />
      <div
        className="relative m-4 w-full max-w-lg rounded-2xl bg-white p-4 shadow-xl dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10"
        onClick={(e) => {
          e.stopPropagation();
          setArmed(null);
          setTzOpen(false);
        }}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              <Calendar size={15} className="text-sky-500" />
              {reschedule ? 'Move their meeting' : 'Book a time'}
              {leadName ? ` - ${leadName}` : ''}
            </div>
            {data?.eventType ? (
              <div className="mt-0.5 text-[11px] font-medium text-slate-400">
                {data.eventType.name}
                {data.eventType.durationMinutes
                  ? ` · ${data.eventType.durationMinutes} min`
                  : ''}{' '}
                · times in {tzAbbr}
                {data.outlook?.checked ? ' · Outlook included' : ''}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-white/10"
          >
            <X size={14} />
          </button>
        </div>

        {successView || loadErrorView || pickerView}
      </div>
    </div>,
    document.body,
  );
};

export default SlotPickerOverlay;
